import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["drawer", "overlay", "body", "badge", "markReadBtn", "clearBtn"]

  connect() {
    this.isOpen = false
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._onKeydown = this._handleKeydown.bind(this)
    document.addEventListener("keydown", this._onKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._onKeydown)
    this.isOpen = false
  }

  _handleKeydown(e) {
    if (e.key === "Escape" && this.isOpen) this.close()
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  open() {
    this.isOpen = true
    this.drawerTarget.classList.add("notif-drawer--open")
    this.overlayTarget.classList.add("notif-drawer-overlay--visible")
    this._loadNotifications()
  }

  close() {
    this.isOpen = false
    this.drawerTarget.classList.remove("notif-drawer--open")
    this.overlayTarget.classList.remove("notif-drawer-overlay--visible")
  }

  async markAllRead() {
    try {
      await fetch("/alerts/mark_read", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          ...(this.csrfToken ? { "X-CSRF-Token": this.csrfToken } : {})
        },
        credentials: "same-origin"
      })

      // Update UI: remove unread styling from all cards
      this.bodyTarget.querySelectorAll(".notif-card--unread").forEach(el => {
        el.classList.remove("notif-card--unread")
      })

      // Hide badge
      if (this.hasBadgeTarget) {
        this.badgeTarget.style.display = "none"
        this.badgeTarget.textContent = ""
      }

      // Hide sidebar badge too
      const sidebarBadge = document.querySelector(".sidebar-badge")
      if (sidebarBadge) sidebarBadge.style.display = "none"

      // Hide mark read button
      if (this.hasMarkReadBtnTarget) {
        this.markReadBtnTarget.style.display = "none"
      }
    } catch (error) {
      console.error("Mark read error:", error)
    }
  }

  async clearAll() {
    try {
      const response = await fetch("/alerts/clear_notifications", {
        method: "DELETE",
        headers: {
          "Accept": "application/json",
          ...(this.csrfToken ? { "X-CSRF-Token": this.csrfToken } : {})
        },
        credentials: "same-origin"
      })
      if (!response.ok) return

      // Clear the drawer body
      this.bodyTarget.innerHTML = `
        <div class="notif-drawer-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>
          <p>No notifications yet</p>
          <span>Notifications appear when your alert conditions are triggered.</span>
        </div>`

      // Hide badge
      if (this.hasBadgeTarget) {
        this.badgeTarget.style.display = "none"
        this.badgeTarget.textContent = ""
      }
      const sidebarBadge = document.querySelector(".sidebar-badge")
      if (sidebarBadge) sidebarBadge.style.display = "none"

      // Hide action buttons
      if (this.hasMarkReadBtnTarget) this.markReadBtnTarget.style.display = "none"
      if (this.hasClearBtnTarget) this.clearBtnTarget.style.display = "none"

      // Update unread counts on alert cards
      document.querySelectorAll(".alert-meta-unread").forEach(el => el.remove())
    } catch (error) {
      console.error("Clear notifications error:", error)
    }
  }

  async _loadNotifications() {
    try {
      const response = await fetch("/alerts/notifications_feed", {
        headers: {
          "Accept": "application/json",
          ...(this.csrfToken ? { "X-CSRF-Token": this.csrfToken } : {})
        },
        credentials: "same-origin"
      })
      if (!response.ok) return
      const notifications = await response.json()
      this._render(notifications)
    } catch (error) {
      this.bodyTarget.innerHTML = '<p class="notif-drawer-error">Could not load notifications.</p>'
    }
  }

  _render(notifications) {
    if (notifications.length === 0) {
      this.bodyTarget.innerHTML = `
        <div class="notif-drawer-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"></path></svg>
          <p>No notifications yet</p>
          <span>Notifications appear when your alert conditions are triggered.</span>
        </div>`
      if (this.hasMarkReadBtnTarget) this.markReadBtnTarget.style.display = "none"
      if (this.hasClearBtnTarget) this.clearBtnTarget.style.display = "none"
      return
    }

    const hasUnread = notifications.some(n => !n.read)
    if (this.hasMarkReadBtnTarget) {
      this.markReadBtnTarget.style.display = hasUnread ? "" : "none"
    }
    if (this.hasClearBtnTarget) {
      this.clearBtnTarget.style.display = ""
    }

    const html = notifications.map(n => {
      const unreadClass = n.read ? "" : " notif-card--unread"
      const alertLink = n.alert_id ? `/alerts#alert-${n.alert_id}` : "/alerts"

      return `
        <div class="notif-card${unreadClass}">
          <div class="notif-card-header">
            <span class="notif-card-name">${this._esc(n.alert_name)}</span>
            <span class="notif-card-time">${this._esc(n.triggered_at)}</span>
          </div>
          <div class="notif-card-summary">${this._esc(n.summary)}</div>
          <a href="${alertLink}" class="notif-card-link" data-action="click->notification-drawer#close">View Alert Results &rarr;</a>
        </div>`
    }).join("")

    this.bodyTarget.innerHTML = html
  }

  _esc(str) {
    if (!str) return ""
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
